// Gate components for CourseView — extracted from course.tsx so the main view
// focuses on the status state machine and ingest orchestration.
//
// Session 47: View monolith decomposition.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, BookOpen, Layers, Loader2 } from 'lucide-react';
import type { CourseDraft } from '@examforge/shared/schemas';
import { HealthBadge } from '../components/health-badge.js';
import { useT, LanguageToggle } from '../i18n/index.js';
import { parseExamDateSafe } from '../lib/days-until.js';

// ── CourseDraftSummary ──────────────────────────────────────────────────────

export function CourseDraftSummary({ draft }: { draft: CourseDraft }) {
  // E-2 (19BM): parseExamDateSafe guards against Invalid Date — schema says
  // datetime().nullable() but spec §3.5 mandates "绝不显示 NaN/Invalid Date".
  const examDate = parseExamDateSafe(draft.examDate);
  return (
    <header className="course-summary">
      <h1>{draft.title}</h1>
      {examDate && (
        <span className="course-summary-date">
          {examDate.toLocaleDateString()}
        </span>
      )}
    </header>
  );
}

// ── CenterMsg ────────────────────────────────────────────────────────────────

export function CenterMsg({
  heading,
  body,
  action,
}: {
  heading: string;
  body?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <main className="course-center-msg">
      <h1>{heading}</h1>
      {body && <p>{body}</p>}
      {action && (
        <button className="btn-primary" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </main>
  );
}

// ── ExtractingView — animated outline-extraction progress ──────────────────

/** Rotating phase hints shown during extraction. Icons stay static; the text
 *  comes from the i18n dictionary (gate.step*). */
const PHASE_KEYS: ReadonlyArray<{ icon: typeof BookOpen; key: 'gate.stepParsing' | 'gate.stepStructure' | 'gate.stepOutline' }> = [
  { icon: BookOpen, key: 'gate.stepParsing' },
  { icon: Layers, key: 'gate.stepStructure' },
  { icon: Sparkles, key: 'gate.stepOutline' },
];
const PHASE_ROTATE_MS = 4_000;

export function ExtractingView({ draft, onCancel }: { draft: CourseDraft; onCancel: () => void }) {
  const t = useT();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [startedAt] = useState(() => Date.now());
  const [, forceTick] = useState(0);

  // Rotate phase hints
  useEffect(() => {
    const id = setInterval(() => setPhaseIdx((i) => (i + 1) % PHASE_KEYS.length), PHASE_ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  // Tick every second for elapsed timer
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const phase = PHASE_KEYS[phaseIdx % PHASE_KEYS.length]!;
  const elapsed = Date.now() - startedAt;
  const Icon = phase.icon;

  return (
    <main className="course-extracting-page">
      {/* Hero — centered, immersive */}
      <div className="course-extracting-hero">
        <div className="course-extracting-orb">
          <div className="course-extracting-orb-ring" />
          <Loader2 size={32} className="course-extracting-orb-icon" />
        </div>
        <h2 className="course-extracting-title">
          {t('course.extractingOutline')}
        </h2>

        {/* Rotating phase hint */}
        <p className="course-extracting-phase" key={phaseIdx}>
          <Icon size={14} aria-hidden="true" />
          <span>{t(phase.key)}</span>
        </p>

        <p className="course-extracting-hint">
          {t('course.extractingOutlineHint')}
        </p>

        {/* Elapsed timer */}
        <span className="course-extracting-elapsed">
          {formatExtractElapsed(elapsed)}
        </span>

        <button className="btn-secondary" onClick={onCancel}>
          {t('course.cancel')}
        </button>
      </div>

      {/* Subtle course name at bottom */}
      <p className="course-extracting-course-name">{draft.title}</p>
    </main>
  );
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** Format ms as "M:SS" for the elapsed timer. */
export function formatExtractElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── CourseGate ────────────────────────────────────────────────────────────────

/**
 * top bar with home link + lang toggle + health since /course/:id no longer
 * inherits from MainLayout (Session 19H decision).
 */
export function CourseGate({ children }: { children: ReactNode }) {
  const t = useT();
  return (
    <div className="course-gate">
      <header className="course-gate-header">
        <Link to="/" className="course-gate-back">
          <ArrowLeft size={14} aria-hidden="true" />
          <span>{t('course.backHome')}</span>
        </Link>
        <div className="header-tools">
          <LanguageToggle />
          <HealthBadge />
        </div>
      </header>
      {children}
    </div>
  );
}
